import { Response, apiAbortController } from '@ui/src';
import api from '../../../api/api';
import { Lock, LockFormValues, LockAuditLog } from '../types';

export const securityApi = api.injectEndpoints({
  endpoints: (build) => ({
    getLocks: build.query<Response<Lock>, string>({
      query: (query) => ({
        url: `/v1/locks${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getLockItem: build.query<{ data: Lock }, string>({
      query: (id) => ({
        url: `/v1/locks/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getLockItemUser: build.query<
      {
        data: {
          id: string;
          profile: {
            firstName: string;
            lastName: string;
          };
        };
      },
      string
    >({
      query: (id) => ({
        url: `/v1/users/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUsersOrganizations: build.query<
      Response<{
        id: string;
        user: {
          id: string;
          profile: {
            firstName: string;
            lastName: string;
          };
        };
      }>,
      {
        search?: string;
        cursor?: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations?status=ACTIVE&take=40&type=INDIVIDUAL&type=COMPANY_OFFICIAL${
          data?.search ? `&search=${data.search}` : ''
        }${data?.cursor ? `&cursor=${data?.cursor}` : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createLock: build.mutation<{ data: Lock }, LockFormValues>({
      query: (body) => ({
        url: '/v1/locks',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateLock: build.mutation<
      { data: Lock },
      { body: LockFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/locks/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getLocksAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getLockItemAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getLocksAuditLogItem: build.query<{ data: LockAuditLog }, string>({
      query: (id) => ({
        url: `/v1/idm-audit-log/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getLocksUsers: build.query<
      Response<{ id: string; email?: string; phone: string }>,
      { phone?: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/users?take=20${data.phone ? `&phone=${data.phone}` : ''}${
          data.cursor ? `&cursor=${data.cursor}` : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetLockItemQuery,
  useLazyGetLocksQuery,
  useCreateLockMutation,
  useUpdateLockMutation,
  useLazyGetLocksAuditLogItemQuery,
  useLazyGetLocksAuditLogsQuery,
  useLazyGetUsersOrganizationsQuery,
  useLazyGetLockItemUserQuery,
  useLazyGetLockItemAuditLogsQuery,
  useLazyGetLocksUsersQuery,
} = securityApi;

export const {
  endpoints: {
    getLocks,
    getLockItem,
    createLock,
    updateLock,
    getLocksAuditLogItem,
    getLocksAuditLogs,
    getUsersOrganizations,
    getLockItemUser,
    getLockItemAuditLogs,
    getLocksUsers,
  },
} = securityApi;
